<template>
  <div v-if="selectedSwiper > 0 && !isPublic" class="w-100">
    <LargeNavbar
      :title="
        selectedSwiper <= (questions as QuestionForm).generalQuestions.length
          ? t('check-in.generalAspects')
          : t('check-in.spacesReview')
      "
      show-back-button
      :show-actions="false"
      @back-button="handleSlideTo(selectedSwiper - 1)"
    />
  </div>

  <swiper @swiper="onSwiper">
    <!-- initial view -->
    <swiper-slide class="swiper-no-swiping w-100 d-flex flex-column step-content">
      <InitialView
        :type="type"
        :user="user"
        @next-step="handleSlideTo(selectedSwiper + 1)"
        :preview="isPublic"
        :rental-unit="rentalUnit ?? undefined"
      />
    </swiper-slide>
    <!-- initial view -->

    <!-- General questions -->
    <swiper-slide
      v-for="(question, idx) in (questions as QuestionForm).generalQuestions"
      :key="question.QuestionId"
      class="swiper-no-swiping w-100 step-content d-flex flex-column"
    >
      <GeneralStepCard
        :booking-id="bookingId"
        :question="question"
        :current-step="idx + 1"
        :total-steps="(questions as QuestionForm).generalQuestions.length"
        :type="type"
        @back="handleSlideTo(selectedSwiper - 1)"
        @continue="handleSlideTo(selectedSwiper + 1)"
      />
    </swiper-slide>
    <!-- General questions -->

    <!-- spaces questions -->
    <swiper-slide class="swiper-no-swiping w-100 divider-steps d-flex flex-column">
      <div></div>
      <div class="d-flex w-100 flex-column gap-xs">
        <ion-img
          :src="spacesReview"
          alt="Welcome Hero"
          class="principal-welcome-image mb-lg"
        ></ion-img>
        <h1 class="font-lg">{{ t('check-in.youHaveFinished') }}</h1>
        <p class="font-sm align-start">
          {{ t('check-in.continueSpaces') }}
        </p>
      </div>
      <ion-button
        @click="handleSlideTo(selectedSwiper + 1)"
        fill="solid"
        expand="block"
        size="large"
        color="accent"
        class="text-lg text-weight-bold w-100 mt-lg"
      >
        {{ t('check-in.startSpaces') }}
      </ion-button>
    </swiper-slide>

    <swiper-slide
      v-for="(spaceQuestion, idx) in (questions as QuestionForm).spacesQuestions"
      :key="idx"
      class="swiper-no-swiping w-100 step-content d-flex flex-column"
    >
      <SpacesStepCard
        :booking-id="bookingId"
        :spaceQuestion="spaceQuestion"
        :current-step="idx + 1"
        :total-steps="(questions as QuestionForm).spacesQuestions.length"
        :type="type"
        @back="handleSlideTo(selectedSwiper - 1)"
        @continue="handleSlideTo(selectedSwiper + 1)"
      />
    </swiper-slide>
    <!-- spaces questions -->

    <!-- suggestions -->
    <swiper-slide class="swiper-no-swiping w-100 step-content d-flex flex-column">
      <div class="d-flex w-100 flex-column gap-xs step pa-xs justify-space-between bg-white">
        <div class="d-flex gap-sm flex-column align-center">
          <h1 class="font-md blue-darker bold-medium">
            {{ t('check-in.suggestions') }}
          </h1>
          <p class="font-sm text-align-left">
            {{ t('check-in.suggestionsText') }}
          </p>
        </div>

        <div>
          <img class="img-question-space border-default my-xs" :src="suggests" />
        </div>

        <div>
          <ion-textarea
            class="text-align-left"
            v-model.trim="(questions as QuestionForm).suggests.comment as string"
            fill="solid"
            :label="t('check-in.writeComment')"
            label-placement="floating"
            :placeholder="t('check-in.writeComment')"
          ></ion-textarea>
        </div>

        <div class="d-flex justify-space-between d-flex justify-space-between mt-xs w-100">
          <ion-button
            fill="clear"
            expand="block"
            size="default"
            color="primary"
            class="text-lg text-weight-bold"
            @click="handleSaveForm"
          >
            {{ t('check-in.skipThis') }}
          </ion-button>

          <ion-button
            :disabled="isLoading"
            fill="solid"
            expand="block"
            size="default"
            color="primary"
            class="text-lg text-weight-bold"
            @click="handleSaveForm"
          >
            <ion-icon slot="end" :icon="chevronForwardOutline" aria-hidden="true"></ion-icon>
            {{ t('global.finish') }}
          </ion-button>
        </div>
        <ion-progress-bar v-if="isLoading" type="indeterminate"></ion-progress-bar>
      </div>
    </swiper-slide>
    <!-- suggestions -->

    <!-- finalStep -->
    <swiper-slide class="swiper-no-swiping">
      <FinalView :type="type" />
    </swiper-slide>
    <!-- finalStep -->
  </swiper>
</template>
<script setup lang="ts">
  import { Swiper, SwiperSlide } from 'swiper/vue';
  import { IonImg, IonButton, IonTextarea, IonProgressBar, IonIcon } from '@ionic/vue';
  import { useI18n } from 'vue-i18n';
  import { ref } from 'vue';
  import { chevronForwardOutline } from 'ionicons/icons';
  import { useToast } from '@/composables/useToast';
  import { DeviceInfo } from '@capacitor/device';
  import { storeToRefs } from 'pinia';
  import { useCheckInStore } from '@/stores/checkin-store';
  import LargeNavbar from '../LargeNavbar.vue';
  import GeneralStepCard from './GeneralStepCard.vue';
  import SpacesStepCard from './SpacesStepCard.vue';
  import { getDeviceInfo } from '@/plugins/device';
  import spacesReview from '@/assets/images/spacesReview.png';
  import suggests from '@/assets/images/suggests.png';
  import { QuestionForm } from '@/interfaces/check/check-in.interface';
  import InitialView from './InitialView.vue';
  import FinalView from './FinalView.vue';
  import { User } from '@/interfaces/auth/auth-store.interface';

  const props = withDefaults(
    defineProps<{
      type: string;
      bookingId: string;
      user: User;
    }>(),
    {},
  );

  const { t } = useI18n();
  const { questions, coords, isChecked, isCheckedOut, isPublic, rentalUnit } =
    storeToRefs(useCheckInStore());
  const { saveCheck } = useCheckInStore();
  const { showToast } = useToast();

  const swiperInstance = ref();
  const selectedSwiper = ref(0);

  const onSwiper = (swiper: any) => {
    swiperInstance.value = swiper;
    swiperInstance.value?.slideTo(selectedSwiper.value);
  };

  const handleSlideTo = (idx: number) => {
    if (idx >= 0) {
      selectedSwiper.value = idx;
      swiperInstance.value?.slideTo(selectedSwiper.value);
    }
  };

  const isLoading = ref(false);

  const handleSaveForm = async () => {
    try {
      isLoading.value = true;
      const device = (await getDeviceInfo()) as DeviceInfo & { lat?: number; lon?: number };
      device.lat = coords.value?.coords.latitude;
      device.lon = coords.value?.coords.longitude;

      const response = await saveCheck(device, props.bookingId, props.type);
      if (response.success) {
        await showToast(response.msg as string);
        handleSlideTo(selectedSwiper.value + 1);

        if (props.type === 'check-in') {
          isChecked.value = true;
        } else {
          isCheckedOut.value = true;
        }
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      isLoading.value = false;
    }
  };
</script>
<style lang="css" scoped>
  .swiper {
    width: 100% !important;
    height: 100%;
  }

  .step {
    border-radius: 16px;
    background: #ffffff;
    min-height: 200px;
    max-height: 700px;
    overflow: auto;
    color: black;
  }

  .step-content {
    justify-content: end;
  }

  .divider-steps {
    justify-content: space-between;
  }

  @media (min-width: 500px) {
    .step-content {
      justify-content: center;
    }
    .divider-steps {
      justify-content: center;
    }
  }
</style>
